import { createReducer } from '@reduxjs/toolkit';
import {
  ApiDevPortalAppStatus,
  AppFormErrors,
  ApplicationAssets,
  ApplicationInfo,
  ApplicationInterface,
  ApplicationRating,
  ApplicationSetup,
  RatingRadioInterface,
  SaveStatusType,
  SubmitStatusType,
} from '../../../interfaces/application';
import {
  getAppsAction,
  getAppAction,
  getAppError,
  editAppInfo,
  editAppSetup,
  editAppAssets,
  editAppRating,
  submitAppAction,
  openDeleteDialog,
  getAppStatusAction,
  deleteAppAction,
  saveDraftAppAction,
  resetApp,
  resetApps,
  addQuickApp,
  addApp,
  editApp,
} from '../actions/AppActions';
import { PROJECT_STATUS } from '../../../interfaces/project';

export interface ReduxAppsState {
  apps?: ApplicationInterface[];
  appsLoaded?: boolean;
  app?: ApplicationInterface;
  appNetworkError: boolean;
  appLoaded?: boolean;
  appStatus?: ApiDevPortalAppStatus;
  saveStatus: SaveStatusType;
  submitStatus: SubmitStatusType;
  info: ApplicationInfo;
  assets: ApplicationAssets;
  rating: RatingRadioInterface;
  openDeleteDialog: boolean;
  deleted: boolean;
  setup: ApplicationSetup;
  errors: AppFormErrors;
  [index: string]:
    | ApplicationInterface[]
    | ApplicationInterface
    | ApplicationInfo
    | ApiDevPortalAppStatus
    | RatingRadioInterface
    | ApplicationAssets
    | SubmitStatusType
    | AppFormErrors
    | SaveStatusType
    | ApplicationSetup
    | boolean
    | undefined;
}

const initialAppStatus: ApiDevPortalAppStatus = {
  isDeletable: false,
  isEditable: false,
  status: PROJECT_STATUS.DRAFT,
  linkedAppId: undefined,
  message: '',
};

const initRating: RatingRadioInterface = {
  first: '',
  second: '',
  third: '',
};

const initialInfo: ApplicationInfo = {
  organisationId: '',
  applicationId: '',
  name: '',
  platforms: [''],
  website: '',
  country: '',
  description: '',
  supportEmail: '',
  privacyUrl: '',
  termsUrl: '',
};

const initialSetup: ApplicationSetup = {
  validRedirectUris: [],
  namespace: '',
  status: PROJECT_STATUS.DRAFT,
};

const initialAssets: ApplicationAssets = {
  appLogoUrl: { fileId: '' },
  screenshotsUrl: [],
};

const initialState: ReduxAppsState = {
  apps: [],
  appsLoaded: false,
  app: undefined,
  appNetworkError: false,
  appStatus: { ...initialAppStatus },
  info: { ...initialInfo },
  setup: { ...initialSetup },
  rating: { ...initRating },
  assets: { ...initialAssets },
  errors: {},
  appLoaded: false,
  saveStatus: SaveStatusType.Saved,
  submitStatus: SubmitStatusType.SubmitReview,
  deleted: false,
  openDeleteDialog: false,
};

export const ratingInitialise = (appRating?: ApplicationRating) => {
  if (appRating && appRating.rating && appRating.rating !== 'TBD') {
    // splits the rating letters and keeps the star if exists
    const letters = appRating.rating.split(/(?!\+)/g);

    const rating: RatingRadioInterface = {
      first: letters[0],
      second: letters[1],
      third: letters[2],
    };

    return rating;
  }
};

const appReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(getAppsAction, (state, action) => ({
      ...state,
      apps: action.payload,
      appsLoaded: true,
    }))
    .addCase(getAppAction, (state, action) => ({
      ...state,
      app: action.payload,
      info: { ...initialInfo, ...action.payload.info },
      setup: { ...initialSetup, ...action.payload.setup },
      rating: { ...initRating, ...ratingInitialise(action.payload.rating) },
      assets: { ...initialAssets, ...action.payload.assets },
      appLoaded: true,
      appNetworkError: false,
      submitStatus:
        action.payload && action.payload.setup.status === PROJECT_STATUS.REVIEW
          ? SubmitStatusType.SubmitUpdate
          : SubmitStatusType.SubmitReview,
    }))
    .addCase(getAppError, (state) => ({
      ...state,
      appNetworkError: true,
    }))
    .addCase(editAppInfo, (state, action) => ({
      ...state,
      info: { ...state.info, [action.payload.name]: action.payload.value },
      saveStatus: SaveStatusType.Save,
      errors: {},
    }))
    .addCase(editAppSetup, (state, action) => ({
      ...state,
      setup: { ...state.setup, [action.payload.name]: action.payload.value },
      saveStatus: SaveStatusType.Save,
      errors: {},
    }))
    .addCase(editAppAssets, (state, action) => ({
      ...state,
      assets: { ...state.assets, [action.payload.name]: action.payload.value },
      saveStatus: SaveStatusType.Save,
      errors: {},
    }))
    .addCase(editAppRating, (state, action) => ({
      ...state,
      rating: { ...state.rating, [action.payload.name]: action.payload.value },
      saveStatus: SaveStatusType.Save,
      errors: {},
    }))
    .addCase(submitAppAction, (state, action) => ({
      ...state,
      app: action.payload,
      info: { ...initialInfo, ...action.payload.info },
      setup: { ...initialSetup, ...action.payload.setup },
      rating: { ...initRating, ...ratingInitialise(action.payload.rating) },
      assets: { ...initialAssets, ...action.payload.assets },
      submitStatus: SubmitStatusType.Submitted,
      appNetworkError: false,
      errors: {},
    }))
    .addCase(openDeleteDialog, (state, action) => ({ ...state, openDeleteDialog: action.payload }))
    .addCase(getAppStatusAction, (state, action) => ({ ...state, appStatus: action.payload }))
    .addCase(deleteAppAction, (state) => ({ ...state, openDeleteDialog: false, deleted: true }))
    .addCase(saveDraftAppAction, (state) => ({
      ...state,
      saveStatus: SaveStatusType.Saved,
      submitStatus:
        state.app && state.app.setup.status === PROJECT_STATUS.REVIEW
          ? SubmitStatusType.SubmitUpdate
          : SubmitStatusType.SubmitReview,
    }))
    .addCase(resetApp, (state) => ({
      ...state,
      app: undefined,
      apps: [],
      appsLoaded: false,
      appStatus: undefined,
      appLoaded: false,
      deleted: false,
      appNetworkError: false,
      openDeleteDialog: false,
      submitStatus: SubmitStatusType.SubmitReview,
      info: { ...initialInfo },
      setup: { ...initialSetup },
      rating: { ...initRating },
      assets: { ...initialAssets },
      errors: {},
    }))
    .addCase(resetApps, (state) => ({ ...state, apps: [], appsLoaded: false }))
    .addCase(addQuickApp, (state, action) => {
      const { payload } = action;

      const newApp: ApplicationInterface = {
        info: {
          applicationId: payload.id,
          name: payload.name,
          country: payload.country,
          platforms: payload.platforms,
          website: payload.website,
          version: payload.version,
        },
        setup: {
          status: PROJECT_STATUS.TEST,
          namespace: payload.namespace,
          validRedirectUris: [],
        },
        dateCreated: `${new Date(Date.now()).toISOString().slice(0, -5)}Z`,
        lastUpdated: `${new Date(Date.now()).toISOString().slice(0, -5)}Z`,
      };

      return {
        ...state,
        apps: state.apps ? [...state.apps, newApp] : [newApp],
      };
    })
    .addCase(addApp, (state, action) => ({
      ...state,
      apps: state.apps ? [...state.apps, action.payload] : [action.payload],
    }))
    .addCase(editApp, (state, action) => {
      if (!state.apps) return state;

      const newApps = state.apps.map((app) => {
        if (app.info.applicationId !== action.payload.info.applicationId) return app;
        return { ...app, ...action.payload };
      });

      return {
        ...state,
        apps: newApps,
      };
    }),
);

export default appReducer;
